import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Bot, Settings, Clock, Check, CheckCheck, AlertCircle } from 'lucide-react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Componente para mostrar un mensaje individual del chat
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.message - Objeto del mensaje
 */
const ChatMessage = ({ message }) => {
  const { t } = useTranslation();

  const {
    id,
    text,
    sender,
    userId,
    timestamp,
    type = 'text',
    isStreaming = false,
    status = 'sent' // 'sent', 'delivered', 'read', 'error'
  } = message;

  // Determinar si es mensaje del usuario actual
  const isOwnMessage = sender === 'user';
  const isAIMessage = sender === 'ai';
  const isSystemMessage = sender === 'system';

  // Clases CSS del mensaje
  const messageClasses = classNames('chat-message', {
    'chat-message--own': isOwnMessage,
    'chat-message--ai': isAIMessage,
    'chat-message--system': isSystemMessage
  });

  // Obtener nombre del remitente
  const getSenderName = () => {
    if (isAIMessage) {
      return 'Abigail-CIA';
    }
    
    if (isSystemMessage) {
      return t('chat.sistema');
    }

    if (isOwnMessage) {
      return t('chat.tu');
    }

    return userId || t('chat.usuario');
  };

  // Formatear timestamp
  const formatTimestamp = (timestamp) => {
    const messageTime = moment(timestamp);
    const now = moment();
    
    if (now.diff(messageTime, 'days') === 0) {
      return messageTime.format('HH:mm');
    } else if (now.diff(messageTime, 'days') === 1) {
      return t('chat.ayer') + ' ' + messageTime.format('HH:mm');
    } else {
      return messageTime.format('DD/MM/YYYY HH:mm');
    }
  };

  // Obtener icono de estado
  const getStatusIcon = () => {
    switch (status) {
      case 'sending':
        return <Clock size={12} className="chat-status-icon chat-status-icon--sending" />;
      case 'sent':
        return <Check size={12} className="chat-status-icon chat-status-icon--sent" />;
      case 'delivered':
        return <CheckCheck size={12} className="chat-status-icon chat-status-icon--delivered" />;
      case 'read':
        return <CheckCheck size={12} className="chat-status-icon chat-status-icon--read" />;
      case 'error':
        return <AlertCircle size={12} className="chat-status-icon chat-status-icon--error" />;
      default:
        return null;
    }
  };

  // Detectar si el contenido es HTML o Markdown
  const isHTMLContent = (text) => {
    const htmlRegex = /<[^>]*>/;
    return htmlRegex.test(text);
  };

  const isMarkdownContent = (text) => {
    const markdownRegex = /(\*\*|__|`|#|\[.*\]\(.*\)|\n-|\n\*|\n\d+\.)/;
    return markdownRegex.test(text);
  };

  // Renderizar contenido del mensaje
  const renderMessageContent = () => {
    return (
      <div className="chat-message-text">
        <div className="chat-message-content-text">
          {isHTMLContent(text) ? (
            <div dangerouslySetInnerHTML={{ __html: text }} />
          ) : isMarkdownContent(text) ? (
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                // Personalizar componentes si es necesario
                code: ({ node, inline, className, children, ...props }) => {
                  return inline ? (
                    <code className="inline-code" {...props}>
                      {children}
                    </code>
                  ) : (
                    <pre className="code-block">
                      <code className={className} {...props}>
                        {children}
                      </code>
                    </pre>
                  );
                },
                a: ({ node, children, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                )
              }}
            >
              {text}
            </ReactMarkdown>
          ) : (
            text
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={messageClasses}>
      <div className="chat-message-wrapper">
        {!isOwnMessage && (
          <div className="chat-message-avatar">
            {isAIMessage && (
              <div className="chat-avatar chat-avatar--ai">
                <div className="chat-avatar-icon">
                  <Bot size={16} />
                </div>
              </div>
            )}
            {isSystemMessage && (
              <div className="chat-avatar chat-avatar--system">
                <div className="chat-avatar-icon">
                  <Settings size={16} />
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="chat-message-content">
          {!isOwnMessage && !isSystemMessage && (
            <div className="chat-message-sender">
              <span className="chat-sender-name">{getSenderName()}</span>
              <span className="chat-message-time">
                {formatTimestamp(timestamp)}
              </span>
            </div>
          )}
          
          <div className="chat-message-bubble">
            {renderMessageContent()}
            
            {(isOwnMessage || isSystemMessage) && (
              <div className="chat-message-meta">
                <span className="chat-message-time">
                  {formatTimestamp(timestamp)}
                </span>
                
                {isOwnMessage && (
                  <span className="chat-message-status">
                    {getStatusIcon()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default ChatMessage;
